import { Controller } from '@hotwired/stimulus';
import Chart from 'chart.js/auto';
import { tourRunning } from '../tours/helpers';

export default class extends Controller {
  static targets = ['carousel'];
  static SUGGESTION_PREFIX = '#suggestion-';

  connect() {
    this.#initializeCarousel();
    this.#handleBasePathNavigation();

    // Wait for carousel to be fully initialized before attempting to navigate
    setTimeout(() => {
      this.#goToSlide();
    }, 50);

    this.#listenForSlideUpdates();
    this.#listenForUrlChanges();
    this.#listenForKeyEvents();
    this.#showSlidesToRenderCorrectChartHeights();
  }

  #initializeCarousel() {
    this.carousel =
      bootstrap.Carousel.getInstance(this.carouselTarget) ||
      new bootstrap.Carousel(this.carouselTarget, {
        ride: false,
        interval: false,
        // Bootstrap's arrow handling duplicates the document listener below, moving two slides per press
        // and slipping past that listener's guards.
        keyboard: false,
      });
  }

  #listenForKeyEvents() {
    document.addEventListener('keydown', (event) => {
      // Moving the deck under a running tour strands its popover on a card that has slid off screen.
      if (tourRunning()) return;

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.carousel.next(); // go forward
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.carousel.prev(); // go back
      }
    });
  }

  #handleBasePathNavigation() {
    if (!window.location.hash) {
      history.replaceState({ slide: 0 }, '', `${this.constructor.SUGGESTION_PREFIX}0`);
    }
  }

  // ChartJS has a known issue where if the chart is not on screen (aka not on the first slide of a carousel), it does not render. We get around this by:
  // 1. Setting all cards to d-inline in the html
  // 2. Setting opacity to 0 in the html to prevent javascript flicker
  // 3. Waiting for the charts to render (with opacity 0)
  // 3. Removing 'd-inline' from all cards
  // 4. Removing the opacity-0 class
  #showSlidesToRenderCorrectChartHeights() {
    requestAnimationFrame(() => {
      const checkChartsReady = () => {
        const charts = document.querySelectorAll('.carousel-item canvas[data-chart]');
        const allChartsInitialized = Array.from(charts).every((canvas) => {
          const chart = Chart.getChart(canvas);
          return chart && chart.initialized;
        });

        if (allChartsInitialized) {
          const slides = document.querySelectorAll('.carousel-item');
          slides.forEach((slide) => {
            const collapseElement = slide.querySelector('.suggestion-card-collapse');
            if (collapseElement) {
              collapseElement.classList.remove('show');
            }
            slide.classList.remove('d-inline');
            slide.classList.remove('opacity-0');
          });
        } else {
          setTimeout(checkChartsReady, 50);
        }
      };

      checkChartsReady();
    });
  }

  #goToSlide() {
    const hash = window.location.hash;
    if (hash && hash.match(new RegExp(`^${this.constructor.SUGGESTION_PREFIX}\\d+$`))) {
      const slideNumber = parseInt(hash.replace(this.constructor.SUGGESTION_PREFIX, ''), 10);
      this.carousel.to(slideNumber);
    }
  }

  #listenForSlideUpdates() {
    this.carouselTarget.addEventListener('slid.bs.carousel', (event) => {
      const newSlideIndex = event.to;

      if (window.location.hash !== `${this.constructor.SUGGESTION_PREFIX}${newSlideIndex}`) {
        history.pushState({ slide: newSlideIndex }, '', `${this.constructor.SUGGESTION_PREFIX}${newSlideIndex}`);
      }
    });
  }

  #listenForUrlChanges() {
    window.addEventListener('popstate', (event) => {
      if (event.state && event.state.slide !== undefined) {
        const slideNumber = event.state.slide;
        this.carousel.to(slideNumber);
      } else if (window.location.hash) {
        // Handle case when popstate event doesn't have state (e.g., when using browser back/forward)
        this.#goToSlide();
      }
    });
  }
}
